import { useEffect, useMemo, useState } from "react";
import "../../src/live-workflow-section.css";

const WORKFLOW_STEPS = [
  {
    id: "message",
    label: "Message",
    title: "Live conversations",
    description:
      "Messages arrive, replies move instantly, and everyone stays inside the same conversation.",
  },
  {
    id: "ticket",
    label: "Ticket",
    title: "Issues become actionable",
    description:
      "A conversation becomes a structured ticket with its context, priority, and ownership attached.",
  },
  {
    id: "task",
    label: "Task",
    title: "Work moves into action",
    description:
      "Next steps are assigned, progress is visible, and every action moves toward completion.",
  },
  {
    id: "remote",
    label: "Remote",
    title: "Support connects live",
    description:
      "Start a secure remote session and investigate the issue directly without losing context.",
  },
  {
    id: "reminder",
    label: "Reminder",
    title: "Nothing gets forgotten",
    description:
      "Follow-ups are scheduled automatically so important work returns at exactly the right time.",
  },
  {
    id: "resolved",
    label: "Resolved",
    title: "The loop closes",
    description:
      "The issue is resolved, the client is updated, and the entire workflow remains connected.",
  },
];

const StepIcon = ({ type }) => {
  const iconProps = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "1.8",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": "true",
  };

  switch (type) {
    case "message":
      return (
        <svg {...iconProps}>
          <path d="M21 11.5a8.4 8.4 0 0 1-9 8.5 9.4 9.4 0 0 1-4-.9L3 20l1.2-4.2A8.3 8.3 0 0 1 3 11.5 8.5 8.5 0 0 1 12 3a8.5 8.5 0 0 1 9 8.5Z" />
          <path d="M8 12h.01M12 12h.01M16 12h.01" />
        </svg>
      );

    case "ticket":
      return (
        <svg {...iconProps}>
          <path d="M4 6h16v4a2 2 0 0 0 0 4v4H4v-4a2 2 0 0 0 0-4V6Z" />
          <path d="M12 8.5v7" strokeDasharray="2 2" />
        </svg>
      );

    case "task":
      return (
        <svg {...iconProps}>
          <path d="M9 6h11M9 12h11M9 18h11" />
          <path d="m3 6 1.5 1.5L7 5M3 12l1.5 1.5L7 11M3 18l1.5 1.5L7 17" />
        </svg>
      );

    case "remote":
      return (
        <svg {...iconProps}>
          <rect x="3" y="4" width="18" height="13" rx="2" />
          <path d="M8 21h8M12 17v4" />
          <path d="m15 8 3 3-3 3M9 14l-3-3 3-3" />
        </svg>
      );

    case "reminder":
      return (
        <svg {...iconProps}>
          <path d="M18 8a6 6 0 0 0-12 0c0 7-3 7-3 9h18c0-2-3-2-3-9" />
          <path d="M10 21h4" />
        </svg>
      );

    case "resolved":
      return (
        <svg {...iconProps}>
          <circle cx="12" cy="12" r="9" />
          <path d="m8 12 2.7 2.7L16.5 9" />
        </svg>
      );

    default:
      return null;
  }
};

const LiveDot = ({ text = "LIVE" }) => (
  <span className="cwf-live-badge">
    <span className="cwf-live-dot" />
    {text}
  </span>
);

function MessagingScene() {
  return (
    <div className="cwf-scene cwf-message-scene">
      <div className="cwf-message-layout">
        <div className="cwf-chat-window">
          <div className="cwf-window-header">
            <div className="cwf-person">
              <div className="cwf-avatar cwf-avatar-coral">NP</div>

              <div>
                <strong>Nina Patel</strong>
                <span>
                  <i />
                  Online now
                </span>
              </div>
            </div>

            <LiveDot />
          </div>

          <div className="cwf-chat-body">
            <div className="cwf-chat-row cwf-chat-in cwf-message-1">
              <div className="cwf-mini-avatar">NP</div>

              <div>
                <div className="cwf-bubble">
                  Hi, I can't access the dashboard after the update.
                </div>

                <span className="cwf-message-time">10:42 AM</span>
              </div>
            </div>

            <div className="cwf-typing cwf-message-typing">
              <span />
              <span />
              <span />
            </div>

            <div className="cwf-chat-row cwf-chat-out cwf-message-2">
              <div>
                <div className="cwf-bubble">
                  Got it. I'm checking the issue now.
                </div>

                <span className="cwf-message-time">
                  10:42 AM
                  <b>✓✓</b>
                </span>
              </div>
            </div>

            <div className="cwf-chat-row cwf-chat-out cwf-message-3">
              <div>
                <div className="cwf-bubble">
                  Can you confirm which dashboard isn't loading?
                </div>

                <span className="cwf-message-time">
                  10:43 AM
                  <b>✓✓</b>
                </span>
              </div>
            </div>

            <div className="cwf-chat-row cwf-chat-in cwf-message-4">
              <div className="cwf-mini-avatar">NP</div>

              <div>
                <div className="cwf-bubble">
                  Analytics dashboard. I'll send the screenshot.
                </div>

                <span className="cwf-message-time">10:43 AM</span>
              </div>
            </div>
          </div>

          <div className="cwf-message-composer">
            <span>Write a message...</span>

            <button type="button" aria-label="Send message">
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="m22 2-7 20-4-9-9-4 20-7Z" />
                <path d="M22 2 11 13" />
              </svg>
            </button>
          </div>
        </div>

        <div className="cwf-live-activity">
          <div className="cwf-panel-label">LIVE MESSAGE ACTIVITY</div>

          <div className="cwf-activity-line cwf-activity-1">
            <span className="cwf-activity-icon">
              <StepIcon type="message" />
            </span>

            <div>
              <strong>Message received</strong>
              <p>Nina Patel · Client support</p>
            </div>

            <time>Now</time>
          </div>

          <div className="cwf-activity-line cwf-activity-2">
            <span className="cwf-activity-icon">
              <StepIcon type="message" />
            </span>

            <div>
              <strong>Support replied</strong>
              <p>Response delivered instantly</p>
            </div>

            <time>+1s</time>
          </div>

          <div className="cwf-activity-line cwf-activity-3">
            <span className="cwf-activity-icon">
              <StepIcon type="message" />
            </span>

            <div>
              <strong>Conversation active</strong>
              <p>Context continuously captured</p>
            </div>

            <time>Live</time>
          </div>

          <div className="cwf-signal-visual">
            <span />
            <span />
            <span />
            <span />
            <span />
            <span />
            <span />
          </div>
        </div>
      </div>
    </div>
  );
}

function TicketScene() {
  return (
    <div className="cwf-scene cwf-ticket-scene">
      <div className="cwf-ticket-layout">
        <div className="cwf-ticket-source">
          <div className="cwf-source-label">FROM CONNECT</div>

          <div className="cwf-source-message">
            <div className="cwf-avatar cwf-avatar-coral">NP</div>

            <div>
              <strong>Nina Patel</strong>
              <p>Client cannot access the dashboard after the update.</p>
            </div>
          </div>

          <div className="cwf-convert-pulse">
            <span />
            <span />
            <span />
          </div>

          <div className="cwf-convert-copy">
            Conversation detected
            <strong>Creating ticket...</strong>
          </div>
        </div>

        <div className="cwf-ticket-main">
          <div className="cwf-ticket-created">
            <span className="cwf-ticket-created-icon">
              <StepIcon type="ticket" />
            </span>

            <span>Ticket created</span>
          </div>

          <div className="cwf-ticket-top">
            <div>
              <span className="cwf-panel-label">DESK</span>
              <h3>TK-2840</h3>
            </div>

            <span className="cwf-high-chip">HIGH</span>
          </div>

          <h4>Client access issue</h4>

          <p className="cwf-ticket-description">
            Conversation linked · 3 messages attached
          </p>

          <div className="cwf-ticket-progress">
            <span />
          </div>

          <div className="cwf-status-flow">
            <div className="cwf-ticket-status cwf-status-open">
              <i />
              OPEN
            </div>

            <div className="cwf-status-arrow">→</div>

            <div className="cwf-ticket-status cwf-status-progress">
              <i />
              IN PROGRESS
            </div>
          </div>

          <div className="cwf-ticket-meta">
            <div>
              <span>Assigned to</span>

              <strong>
                <span className="cwf-avatar cwf-avatar-green">JK</span>
                Jai Kumar
              </strong>
            </div>

            <div>
              <span>Source</span>
              <strong>Connect</strong>
            </div>

            <div>
              <span>Messages</span>
              <strong>03 linked</strong>
            </div>
          </div>
        </div>

        <div className="cwf-ticket-events">
          <div className="cwf-panel-label">TICKET ACTIVITY</div>

          <div className="cwf-ticket-event cwf-ticket-event-1">
            <span>01</span>
            <div>
              <strong>TK-2840 generated</strong>
              <p>Conversation linked automatically</p>
            </div>
          </div>

          <div className="cwf-ticket-event cwf-ticket-event-2">
            <span>02</span>
            <div>
              <strong>Priority set to High</strong>
              <p>Urgency detected from conversation</p>
            </div>
          </div>

          <div className="cwf-ticket-event cwf-ticket-event-3">
            <span>03</span>
            <div>
              <strong>Assigned to Jai Kumar</strong>
              <p>Ticket moved to In Progress</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function TaskScene() {
  return (
    <div className="cwf-scene cwf-task-scene">
      <div className="cwf-task-layout">
        <div className="cwf-task-summary">
          <div className="cwf-panel-label">NEXT ACTION</div>

          <span className="cwf-task-count">01 TASK</span>

          <h3>Verify client environment</h3>

          <p>
            Check the client environment and identify why the analytics dashboard
            isn't loading.
          </p>

          <div className="cwf-task-owner">
            <span className="cwf-avatar cwf-avatar-green">JK</span>

            <div>
              <span>ASSIGNED TO</span>
              <strong>Jai Kumar</strong>
            </div>

            <LiveDot text="ON IT" />
          </div>
        </div>

        <div className="cwf-task-board">
          <div className="cwf-task-board-header">
            <div>
              <span className="cwf-panel-label">TASK PROGRESS</span>
              <h4>Client environment check</h4>
            </div>

            <strong className="cwf-percent">100%</strong>
          </div>

          <div className="cwf-task-row cwf-task-row-1">
            <span className="cwf-task-check">
              <svg viewBox="0 0 20 20">
                <path d="m5 10 3 3 7-7" />
              </svg>
            </span>

            <div>
              <strong>Verify dashboard availability</strong>
              <p>Check application response</p>
            </div>

            <span className="cwf-task-status-text">Done</span>
          </div>

          <div className="cwf-task-row cwf-task-row-2">
            <span className="cwf-task-check">
              <svg viewBox="0 0 20 20">
                <path d="m5 10 3 3 7-7" />
              </svg>
            </span>

            <div>
              <strong>Check client configuration</strong>
              <p>Validate session and user permissions</p>
            </div>

            <span className="cwf-task-status-text">Done</span>
          </div>

          <div className="cwf-task-row cwf-task-row-3">
            <span className="cwf-task-check">
              <svg viewBox="0 0 20 20">
                <path d="m5 10 3 3 7-7" />
              </svg>
            </span>

            <div>
              <strong>Start remote investigation</strong>
              <p>Open secure Assist session</p>
            </div>

            <span className="cwf-task-status-text">Done</span>
          </div>

          <div className="cwf-task-progress-track">
            <span />
          </div>

          <div className="cwf-task-progress-labels">
            <span>0%</span>
            <span>Task running...</span>
            <span>100%</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function RemoteScene() {
  return (
    <div className="cwf-scene cwf-remote-scene">
      <div className="cwf-remote-layout">
        <div className="cwf-remote-info">
          <div className="cwf-panel-label">ASSIST · SECURE SESSION</div>

          <h3>Connect with Remote ID and passcode.</h3>

          <p>
            Jai starts a secure Assist session by entering the client’s Remote ID,
            adding the one-time passcode, requesting approval, and then connecting
            live to the user’s system.
          </p>

          <div className="cwf-remote-status-list">
            <div className="cwf-remote-status cwf-remote-status-1">
              <span />
              Remote ID entered
            </div>

            <div className="cwf-remote-status cwf-remote-status-2">
              <span />
              One-time passcode verified
            </div>

            <div className="cwf-remote-status cwf-remote-status-3">
              <span />
              Client approval received
            </div>

            <div className="cwf-remote-status cwf-remote-status-4">
              <span />
              Secure remote session connected
            </div>
          </div>
        </div>

        <div className="cwf-monitor-shell">
          <div className="cwf-monitor-top">
            <div className="cwf-monitor-dots">
              <span />
              <span />
              <span />
            </div>

            <LiveDot text="SECURE CONNECT" />
          </div>

          <div className="cwf-monitor-screen">
            <div className="cwf-remote-sidebar">
              <span className="cwf-sidebar-logo" />
              <span />
              <span />
              <span />
              <span />
            </div>

            <div className="cwf-remote-content cwf-remote-connect-desktop">
              <div className="cwf-remote-connect-grid">
                <div className="cwf-connect-card">
                  <div className="cwf-connect-card__top">
                    <div>
                      <small>Quick remote access</small>
                      <strong>Connect to client device</strong>
                    </div>

                    <span className="cwf-remote-live-chip">
                      <i />
                      LIVE REQUEST
                    </span>
                  </div>

                  <div className="cwf-connect-field cwf-connect-field-id">
                    <label>Remote ID</label>
                    <div className="cwf-connect-input">
                      <span>847</span>
                      <span>295</span>
                      <span>631</span>
                    </div>
                  </div>

                  <div className="cwf-connect-field cwf-connect-field-passcode">
                    <label>One-time passcode</label>
                    <div className="cwf-connect-input cwf-connect-input--passcode">
                      <span />
                      <span />
                      <span />
                      <span />
                      <span />
                      <span />
                    </div>
                  </div>

                  <div className="cwf-connect-approval">
                    <div className="cwf-connect-approval__avatar">NP</div>

                    <div>
                      <strong>Nina Patel</strong>
                      <span>Waiting for permission on Nina-PC</span>
                    </div>

                    <em>Approve</em>
                  </div>

                  <button type="button" className="cwf-connect-button">
                    Start Secure Session
                  </button>
                </div>

                <div className="cwf-connection-journey">
                  <div className="cwf-connection-journey__top">
                    <strong>Connection Status</strong>
                    <span>Assist handshake</span>
                  </div>

                  <div className="cwf-connection-step cwf-connection-step-1">
                    <i />
                    <div>
                      <strong>Remote ID recognized</strong>
                      <small>Matched to Nina-PC</small>
                    </div>
                  </div>

                  <div className="cwf-connection-step cwf-connection-step-2">
                    <i />
                    <div>
                      <strong>Passcode accepted</strong>
                      <small>One-time code verified</small>
                    </div>
                  </div>

                  <div className="cwf-connection-step cwf-connection-step-3">
                    <i />
                    <div>
                      <strong>User approval granted</strong>
                      <small>Client accepted the request</small>
                    </div>
                  </div>

                  <div className="cwf-connection-step cwf-connection-step-4">
                    <i />
                    <div>
                      <strong>Connected to remote desktop</strong>
                      <small>Encrypted session now active</small>
                    </div>
                  </div>

                  <div className="cwf-connection-progress">
                    <span />
                  </div>
                </div>
              </div>

              <div className="cwf-live-remote-preview">
                <div className="cwf-live-remote-preview__header">
                  <div>
                    <strong>Nina-PC</strong>
                    <small>Windows 11 Pro · Remote desktop ready</small>
                  </div>

                  <span className="cwf-preview-status">
                    <i />
                    Connected
                  </span>
                </div>

                <div className="cwf-live-remote-preview__screen">
                  <div className="cwf-preview-toolbar">
                    <span />
                    <span />
                    <span />
                  </div>

                  <div className="cwf-preview-window">
                    <div className="cwf-preview-window__title">
                      <i />
                      <strong>Support Console</strong>
                    </div>

                    <div className="cwf-preview-window__body">
                      <div className="cwf-preview-metric">
                        <small>Session</small>
                        <strong>Secure</strong>
                      </div>

                      <div className="cwf-preview-metric">
                        <small>Control</small>
                        <strong>Enabled</strong>
                      </div>

                      <div className="cwf-preview-metric">
                        <small>Latency</small>
                        <strong>24 ms</strong>
                      </div>

                      <div className="cwf-preview-metric">
                        <small>User</small>
                        <strong>Nina Patel</strong>
                      </div>
                    </div>
                  </div>

                  <div className="cwf-preview-toast">
                    <span />
                    Remote desktop connected successfully
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="cwf-remote-controls">
            <div>
              <span className="cwf-avatar cwf-avatar-green">JK</span>

              <div>
                <strong>Jai Kumar</strong>
                <span>Connected to Nina Patel</span>
              </div>
            </div>

            <div className="cwf-control-buttons">
              <button type="button">⌁</button>
              <button type="button">◉</button>
              <button type="button" className="cwf-end-call">
                ×
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ReminderScene() {
  return (
    <div className="cwf-scene cwf-reminder-scene">
      <div className="cwf-reminder-layout">
        <div className="cwf-reminder-main">
          <div className="cwf-reminder-header">
            <span className="cwf-reminder-bell">
              <StepIcon type="reminder" />
              <i />
              <i />
            </span>

            <div>
              <span className="cwf-panel-label">MY SPACE</span>
              <h3>Follow-up reminder</h3>
            </div>
          </div>

          <div className="cwf-reminder-time">
            <strong>2:00</strong>

            <div>
              <span>PM</span>
              <b>TODAY</b>
            </div>
          </div>

          <p>
            Confirm client access and send the final resolution update to Nina
            Patel.
          </p>

          <div className="cwf-reminder-toggle-row">
            <div>
              <span>Reminder active</span>
              <small>Notify me when it's time</small>
            </div>

            <span className="cwf-reminder-toggle">
              <i />
            </span>
          </div>
        </div>

        <div className="cwf-reminder-timeline">
          <div className="cwf-panel-label">FOLLOW-UP TIMELINE</div>

          <div className="cwf-time-item cwf-time-item-1">
            <time>10:45 AM</time>

            <span />

            <div>
              <strong>Reminder created</strong>
              <p>Follow-up scheduled for 2:00 PM</p>
            </div>
          </div>

          <div className="cwf-time-item cwf-time-item-2">
            <time>1:59 PM</time>

            <span />

            <div>
              <strong>Follow-up approaching</strong>
              <p>My Space prepares the reminder</p>
            </div>
          </div>

          <div className="cwf-time-item cwf-time-item-3">
            <time>2:00 PM</time>

            <span />

            <div>
              <strong>Time to follow up</strong>
              <p>Reminder delivered to Jai Kumar</p>
            </div>
          </div>
        </div>

        <div className="cwf-notification-toast">
          <span className="cwf-toast-icon">
            <StepIcon type="reminder" />
          </span>

          <div>
            <strong>Follow-up reminder</strong>
            <p>Confirm client access and send final update.</p>
          </div>

          <time>Now</time>
        </div>
      </div>
    </div>
  );
}

function ResolvedScene() {
  return (
    <div className="cwf-scene cwf-resolved-scene">
      <div className="cwf-resolved-layout">
        <div className="cwf-resolution-visual">
          <div className="cwf-resolution-ring cwf-resolution-ring-1" />
          <div className="cwf-resolution-ring cwf-resolution-ring-2" />
          <div className="cwf-resolution-ring cwf-resolution-ring-3" />

          <div className="cwf-resolution-check">
            <svg viewBox="0 0 100 100">
              <path
                className="cwf-check-path"
                d="M27 51 43 67 75 34"
                fill="none"
                stroke="currentColor"
                strokeWidth="8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>

          <span className="cwf-success-particle cwf-success-particle-1" />
          <span className="cwf-success-particle cwf-success-particle-2" />
          <span className="cwf-success-particle cwf-success-particle-3" />
          <span className="cwf-success-particle cwf-success-particle-4" />
          <span className="cwf-success-particle cwf-success-particle-5" />
          <span className="cwf-success-particle cwf-success-particle-6" />
        </div>

        <div className="cwf-resolution-copy">
          <span className="cwf-resolved-label">WORKFLOW COMPLETE</span>

          <h3>Issue resolved.</h3>

          <p>
            Client access is restored, Nina confirmed the fix, and the complete
            workflow remains connected to TK-2840.
          </p>

          <div className="cwf-resolution-summary">
            <div className="cwf-resolution-item cwf-resolution-item-1">
              <span>
                <StepIcon type="message" />
              </span>

              <div>
                <strong>Conversation retained</strong>
                <p>3 messages linked</p>
              </div>
            </div>

            <div className="cwf-resolution-item cwf-resolution-item-2">
              <span>
                <StepIcon type="ticket" />
              </span>

              <div>
                <strong>TK-2840 closed</strong>
                <p>Status changed to Resolved</p>
              </div>
            </div>

            <div className="cwf-resolution-item cwf-resolution-item-3">
              <span>
                <StepIcon type="resolved" />
              </span>

              <div>
                <strong>Client confirmed</strong>
                <p>Access restored successfully</p>
              </div>
            </div>
          </div>

          <div className="cwf-feedback">
            <div>
              <span>CLIENT FEEDBACK</span>
              <strong>Excellent support!</strong>
            </div>

            <div className="cwf-stars">★★★★★</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function WorkflowScene({ activeStep }) {
  switch (activeStep) {
    case "message":
      return <MessagingScene />;

    case "ticket":
      return <TicketScene />;

    case "task":
      return <TaskScene />;

    case "remote":
      return <RemoteScene />;

    case "reminder":
      return <ReminderScene />;

    case "resolved":
      return <ResolvedScene />;

    default:
      return null;
  }
}

export default function LiveWorkflowSection() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [manualSelection, setManualSelection] = useState(false);
  const [interactionId, setInteractionId] = useState(0);

  const activeStep = WORKFLOW_STEPS[activeIndex];

  const activeProgressDuration = 5000;

  const sceneKey = useMemo(
    () => `${activeStep.id}-${interactionId}`,
    [activeStep.id, interactionId]
  );

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setManualSelection(false);

      setActiveIndex(
        (currentIndex) =>
          (currentIndex + 1) % WORKFLOW_STEPS.length
      );

      setInteractionId((current) => current + 1);
    }, 5000);

    return () => window.clearTimeout(timer);
  }, [activeIndex, manualSelection, interactionId]);

  const selectWorkflow = (index) => {
    if (index === activeIndex) return;

    setActiveIndex(index);
    setManualSelection(true);
    setInteractionId((current) => current + 1);
  };
  return (
    <section className="cwf-section" id="workflow">
      <div className="cwf-background-glow cwf-background-glow-left" />
      <div className="cwf-background-glow cwf-background-glow-right" />

      <div className="cwf-container">
        <div className="cwf-heading">
          <span className="cwf-eyebrow">
            <span />
            HOW IT WORKS TOGETHER
          </span>

          <h2>
            One Workflow.
            <span>Zero Switching.</span>
          </h2>

          <p>
           Cullath is a unified communication and collaboration platform that brings <a href="https://cullath.com/connect"
              className="highlight-link">
            business communication, 
            </a> 
           <a href="https://cullath.com/desk"
              className="highlight-link">
             workflow management
            </a> , <a href="https://cullath.com/connect"
              className="highlight-link">
              messaging,
            </a>and <a href="https://cullath.com/assist"
              className="highlight-link">
             remote support 
            </a> into one connected workspace. From the first 
           conversation to the final resolution, every interaction happens within a single platform.
          </p>
        </div>

        <div className="cwf-workflow-shell">
          <nav className="cwf-step-navigation" aria-label="Workflow steps">
            {WORKFLOW_STEPS.map((step, index) => {
              const isActive = index === activeIndex;

              return (
                <button
                  type="button"
                  key={step.id}
                  className={`cwf-step-button ${
                    isActive ? "cwf-step-button-active" : ""
                  }`}
                  onMouseEnter={() => selectWorkflow(index)}
                  onClick={() => selectWorkflow(index)}
                  aria-pressed={isActive}
                >
                  <span className="cwf-step-icon">
                    <StepIcon type={step.id} />
                  </span>

                  <span className="cwf-step-label">{step.label}</span>

                  {isActive && (
                    <span
                      key={`progress-${interactionId}`}
                      className="cwf-step-progress"
                      style={{
                        "--cwf-progress-duration": `${activeProgressDuration}ms`,
                      }}
                    />
                  )}
                </button>
              );
            })}
          </nav>

          <div className="cwf-active-workflow-header">
            <div>
              <span className="cwf-current-step">
                STEP {String(activeIndex + 1).padStart(2, "0")} OF{" "}
                {String(WORKFLOW_STEPS.length).padStart(2, "0")}
              </span>

              <h3>{activeStep.title}</h3>

              <p>{activeStep.description}</p>
            </div>

            <div className="cwf-now-happening">
              <span className="cwf-now-pulse">
                <i />
              </span>

              <div>
                <span>NOW HAPPENING</span>
                <strong>{activeStep.label} workflow</strong>
              </div>
            </div>
          </div>

          <div className="cwf-animation-stage">
            <div
              key={sceneKey}
              className={`cwf-scene-transition cwf-scene-${activeStep.id}`}
            >
              <WorkflowScene activeStep={activeStep.id} />
            </div>
          </div>

          <footer className="cwf-stage-footer">
            <div className="cwf-footer-step">
              <span className="cwf-footer-icon">
                <StepIcon type={activeStep.id} />
              </span>

              <div>
                <span>ACTIVE WORKFLOW</span>
                <strong>{activeStep.label}</strong>
              </div>
            </div>

            <div className="cwf-footer-status">
              <span>
                <i />
                Context connected
              </span>

              <span>
                <i />
                Live activity
              </span>

              <span>
                <i />
                One continuous workflow
              </span>
            </div>
          </footer>
        </div>
      </div>
    </section>
  );
}